import { useEffect, useRef, useState } from 'react'
import type { AssetDTO, WhiteLabelConfigDTO } from 'api-models'
import { Assets } from 'services/user/assets'
import { ScheduledReports } from 'services/user/scheduled-reports'
import {
	DEFAULT_BRAND_COLOR,
	WhiteLabelBranding
} from '../store/use-report-layout-store'

const fromConfig = (
	config?: WhiteLabelConfigDTO | null
): WhiteLabelBranding => ({
	enabled: !!config?.enabled,
	logoSource:
		config?.logoSource === 'upload' ? 'custom' : config?.logoSource ?? 'custom',
	customLogo: config?.uploadedLogoUrl ?? null,
	brandColor: config?.brandColor ?? DEFAULT_BRAND_COLOR
})

// Scoped to the keyed drawer instance. Read again on open so other users' saves
// appear here too; never seed this from the old browser-wide layout cache.
export const useWebsiteBranding = (
	accountId: string | undefined,
	assetId: string | undefined,
	open: boolean
) => {
	const [asset, setAsset] = useState<AssetDTO | null>(null)
	const [saved, setSaved] = useState(() => fromConfig())
	const [ready, setReady] = useState(false)
	const [error, setError] = useState(false)
	const [saving, setSaving] = useState(false)
	const [revision, setRevision] = useState(0)
	const scope = `${accountId}/${assetId}`
	const latestScope = useRef(scope)
	latestScope.current = scope
	const busy = useRef(false)
	const generation = useRef(0)
	useEffect(() => {
		const current = ++generation.current
		setReady(false)
		setError(false)
		if (open && (!accountId || !assetId)) {
			// The auth-free preview can render defaults but cannot persist branding.
			setSaved(fromConfig())
			setReady(true)
		}
		if (open && accountId && assetId) {
			Assets.getById(accountId, assetId)
				.then((website) => {
					if (generation.current !== current) return
					setAsset(website)
					setSaved(fromConfig(website.white_label))
					setReady(true)
				})
				.catch(() => {
					if (generation.current === current) setError(true)
				})
		}
		return () => {
			generation.current++
		}
	}, [accountId, assetId, open, revision])

	const save = async (draft: WhiteLabelBranding, file: File | null) => {
		if (!ready || busy.current || !accountId || !assetId) return false
		const current = generation.current
		busy.current = true
		setSaving(true)
		try {
			const uploadedLogoUrl =
				draft.logoSource === 'custom' && file
					? await ScheduledReports.uploadWhiteLabelLogo(
							accountId,
							assetId,
							file
					  )
					: undefined
			const config = await ScheduledReports.saveWhiteLabel(accountId, assetId, {
				enabled: draft.enabled,
				logoSource: draft.logoSource === 'custom' ? 'upload' : draft.logoSource,
				brandColor: draft.brandColor,
				...(uploadedLogoUrl ? { uploadedLogoUrl } : {})
			})
			if (generation.current !== current) {
				// Closing/reopening during a save may have fetched the pre-save record.
				if (latestScope.current === scope) setRevision((value) => value + 1)
				return false
			}
			setSaved(fromConfig(config))
			return true
		} finally {
			busy.current = false
			setSaving(false)
		}
	}
	return { asset, saved, ready, error, saving, save }
}
